import React from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBell,
  FaBookOpen,
  FaChartLine,
  FaCheckCircle,
  FaClipboardCheck,
  FaCloud,
  FaCompass,
  FaDatabase,
  FaGraduationCap,
  FaLayerGroup,
  FaMoneyCheckAlt,
  FaShieldAlt,
  FaUsers,
} from "react-icons/fa";
import { getDashboardPathForRole, normalizeRole } from "../utils/authRole";
import Seo from "./Seo";

const platformImage = require("../assets/growing.jpg");

const pillars = [
  {
    title: "Connected operations",
    description: "Admissions, academics, fees, communication, and reports stay aligned instead of living in separate tools.",
    icon: FaLayerGroup,
    tone: "from-blue-600 to-indigo-600",
  },
  {
    title: "Role-based clarity",
    description: "Students, teachers, parents, admins, and school leaders see workflows that match their daily responsibilities.",
    icon: FaUsers,
    tone: "from-teal-600 to-emerald-600",
  },
  {
    title: "Reliable control",
    description: "Structured access, clean records, and dependable dashboards help school teams move with confidence.",
    icon: FaShieldAlt,
    tone: "from-amber-500 to-orange-500",
  },
];

const stats = [
  { label: "Core Focus", value: "School ERP" },
  { label: "Users", value: "Students to admins" },
  { label: "Modules", value: "Admissions, fees, academics" },
  { label: "Experience", value: "Clear and role-aware" },
];

const workflow = [
  {
    title: "Capture the journey",
    description: "Start with enquiries, admissions, profiles, student records, and the first operational handoff.",
    icon: FaCompass,
  },
  {
    title: "Run daily school work",
    description: "Coordinate attendance, timetable, classes, exams, fee updates, and parent communication from one portal.",
    icon: FaClipboardCheck,
  },
  {
    title: "See what needs attention",
    description: "Use dashboards and reports to track progress, dues, activity, communication, and school operations.",
    icon: FaChartLine,
  },
];

const modules = [
  { label: "Admissions", icon: FaGraduationCap },
  { label: "Student records", icon: FaDatabase },
  { label: "Fee tracking", icon: FaMoneyCheckAlt },
  { label: "Academics", icon: FaBookOpen },
  { label: "Notifications", icon: FaBell },
  { label: "Cloud access", icon: FaCloud },
];

const audienceStories = [
  {
    title: "For school leadership",
    detail: "See the operational picture across admissions, academics, collections and communication without waiting for manually assembled updates.",
  },
  {
    title: "For administrators and office teams",
    detail: "Turn repeat work such as student records, applications, payments, notices and follow-ups into clear, trackable workflows.",
  },
  {
    title: "For teachers, students and families",
    detail: "Give each group a focused portal for class activity, learning material, progress and timely school communication.",
  },
];

const aboutSeoKeywords = [
  "about ClassNX",
  "school management software company",
  "school ERP platform",
  "education ERP software",
  "school administration software",
  "student information system",
  "school operations software",
  "role based school portal",
  "school management system for schools",
];

export default function AboutUs() {
  const currentRole = normalizeRole(localStorage.getItem("user"));
  const isLoggedIn = Boolean(currentRole);
  const dashboardPath = getDashboardPathForRole(currentRole);
  const primaryActionPath = isLoggedIn ? dashboardPath : "/services";
  const primaryActionLabel = isLoggedIn ? "Open Dashboard" : "Explore Services";
  const secondaryActionPath = isLoggedIn ? "/profile" : "/login";
  const secondaryActionLabel = isLoggedIn ? "View Profile" : "Open Platform";

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.14),_transparent_30%),linear-gradient(135deg,_#f8fafc_0%,_#eff6ff_48%,_#ecfdf5_100%)] text-slate-900">
      <Seo
        title="About ClassNX - School ERP and Management Software"
        description="Learn how ClassNX creates role-based school ERP and school management software for students, teachers, admins, parents, and school operators."
        canonical="/about"
        keywords={aboutSeoKeywords}
      />

      <section className="px-4 py-12 sm:px-6 md:py-16">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1.02fr_0.98fr] lg:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white/80 px-4 py-2 text-xs font-bold uppercase tracking-[0.24em] text-blue-700 shadow-sm">
              <FaCheckCircle className="text-teal-600" />
              About ClassNX
            </span>
            <h1 className="mt-5 max-w-3xl text-4xl font-black leading-tight tracking-tight text-slate-950 md:text-6xl">
              A calmer operating layer for modern schools.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-8 text-slate-600 md:text-lg">
              ClassNX brings school operations into one connected platform. It helps teams manage admissions, student information, academics, fees, attendance, communication, and reporting with a cleaner day-to-day experience.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to={primaryActionPath}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-700 to-teal-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-700/20 transition hover:-translate-y-0.5 hover:shadow-xl"
              >
                {primaryActionLabel} <FaArrowRight className="text-xs" />
              </Link>
              <Link
                to={secondaryActionPath}
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
              >
                {secondaryActionLabel}
              </Link>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {stats.map((stat) => (
                <div key={stat.label} className="rounded-xl border border-slate-200 bg-white/85 p-4 shadow-sm shadow-slate-200/70">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">{stat.label}</p>
                  <p className="mt-2 text-base font-extrabold text-slate-950">{stat.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border border-white/80 bg-white shadow-[0_30px_80px_-45px_rgba(15,23,42,0.55)]">
            <img
              src={platformImage}
              alt="Student growth journey supported by ClassNX"
              className="h-72 w-full object-cover sm:h-96"
            />
            <div className="border-t border-slate-100 bg-white px-6 py-5">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-teal-700">Built for the full school journey</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                From first enquiry to everyday learning, ClassNX keeps the operational story easy to follow.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 px-4 py-12 text-white sm:px-6 md:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-cyan-300">Who the application supports</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">One platform, with a useful place for every school stakeholder.</h2>
            <p className="mt-4 text-sm leading-7 text-slate-300 md:text-base">ClassNX is designed to connect the people who run a school with the people who learn and communicate in it—without giving everyone the same complicated workspace.</p>
          </div>
          <div className="mt-8 grid gap-5 lg:grid-cols-3">
            {audienceStories.map((story) => (
              <article key={story.title} className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
                <h3 className="text-xl font-black text-white">{story.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-300">{story.detail}</p>
              </article>
            ))}
          </div>
          <div className="mt-8 grid gap-4 rounded-3xl border border-cyan-300/15 bg-cyan-300/5 p-6 md:grid-cols-3">
            <div><p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-200">Discover</p><p className="mt-2 text-sm leading-6 text-slate-300">Map your current processes and identify the modules that will have the greatest immediate impact.</p></div>
            <div><p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-200">Configure</p><p className="mt-2 text-sm leading-6 text-slate-300">Organize roles, school structure and operational information around the way your team actually works.</p></div>
            <div><p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-200">Grow</p><p className="mt-2 text-sm leading-6 text-slate-300">Add connected workflows as your team is ready, keeping the platform useful rather than overwhelming.</p></div>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-12 sm:px-6 md:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-amber-600">What guides the product</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950 md:text-4xl">
              Practical software for real school teams.
            </h2>
            <p className="mt-3 text-sm leading-7 text-slate-600 md:text-base">
              The platform is designed around common school workflows, not around visual noise. Each role gets a clear path to the information and action it needs.
            </p>
          </div>

          <div className="mt-8 grid gap-5 lg:grid-cols-3">
            {pillars.map((pillar) => {
              const Icon = pillar.icon;

              return (
                <article key={pillar.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm transition hover:-translate-y-1 hover:border-blue-200 hover:bg-white hover:shadow-lg">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${pillar.tone} text-white shadow-lg shadow-slate-200`}>
                    <Icon />
                  </div>
                  <h3 className="mt-5 text-xl font-black text-slate-950">{pillar.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{pillar.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[linear-gradient(135deg,_#0f172a_0%,_#1e3a8a_48%,_#0f766e_100%)] px-4 py-12 text-white sm:px-6 md:py-16">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-amber-200">How ClassNX fits</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">
              One workflow from admission to reporting.
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-200 md:text-base">
              School work moves through many people. ClassNX keeps that movement visible, organized, and easier to hand over between teams.
            </p>
          </div>

          <div className="grid gap-4">
            {workflow.map((item, index) => {
              const Icon = item.icon;

              return (
                <article key={item.title} className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
                  <div className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-amber-200">
                      <Icon />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-300">Step {index + 1}</p>
                      <h3 className="mt-1 text-lg font-bold text-white">{item.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-slate-300">{item.description}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 py-12 sm:px-6 md:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-700">What stays connected</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950 md:text-4xl">
                The modules schools expect in one place.
              </h2>
            </div>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-5 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-50"
            >
              View services <FaArrowRight className="text-xs" />
            </Link>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {modules.map((module) => {
              const Icon = module.icon;

              return (
                <article key={module.label} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:border-teal-200 hover:shadow-lg">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                    <Icon />
                  </div>
                  <h3 className="text-base font-bold text-slate-950">{module.label}</h3>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-12 sm:px-6 md:py-16">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 rounded-3xl border border-slate-200 bg-slate-950 px-6 py-8 text-white shadow-[0_30px_90px_-50px_rgba(15,23,42,0.65)] md:flex-row md:items-center md:justify-between md:px-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-amber-300">Next step</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight">See ClassNX in motion.</h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300">
              Explore the modules, review the workflows, and move from a public overview into the platform experience.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-amber-300"
            >
              Explore services <FaArrowRight className="text-xs" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              Back home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
